/* eslint-disable no-nested-ternary */
import { gql, useQuery } from "@apollo/client";
import {
  Button,
  makeStyles,
  Paper,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { usePollingIntervalConfig } from "client/utils/polling";
import Loading from "components/legacy/Loading/Loading";
import {
  GetAvailabilitySchedulerDataForPitchPage,
  GetAvailabilitySchedulerDataForPitchPageVariables,
} from "generated/schemaTypes";
import calendarIcon from "icons/calendar-colorful.svg";
import moment, { Moment } from "moment-timezone";
import Image from "next/image";
import React, { useEffect, useState } from "react";
import useAnalytics from "react-hooks/useAnalytics";
import { ResourceCustomTheme } from "styles/config";

import AvailabilityInterviewScheduler from "./AvailabilityInterviewScheduler";
import NylasSchedulerModal, {
  RedirectParams,
  SchedulerMode,
} from "./NylasSchedulerModal";

const AVAILABILITY_SCHEDULER_DATA = gql`
  query GetAvailabilitySchedulerDataForPitchPage(
    $editHash: UUID
    $pitchPageId: UUID
  ) {
    schedulerAvailabilityEventFindFirst(
      where: { pitchPageId: $pitchPageId, externalEditHash: $editHash }
    ) {
      ...SchedulerAvailabilityDataAvailabilityEvent
    }
  }
  ${AvailabilityInterviewScheduler.fragments.schedulerAvailabilityEvent}
`;

const useStyles = makeStyles((theme: ResourceCustomTheme) => ({
  root: {
    ...theme.mixins.moduleStyles(theme),
  },
  card: {
    padding: `${theme.spacing(4)}px ${theme.spacing(2)}px ${theme.spacing(
      4
    )}px ${theme.spacing(2)}px`,
    border: `1px solid rgba(0,0,0,0.1)`,
    borderRadius: 8,
  },
  infoHeader: {
    display: "flex",
    justifyContent: "stretch",
    alignItems: "center",
    marginBottom: theme.spacing(4),
    paddingLeft: 0,
    paddingRight: 0,
    [theme.breakpoints.up("sm")]: {
      paddingLeft: theme.spacing(4),
      paddingRight: theme.spacing(4),
    },
  },
  infoText: {
    margin: 0,
    fontSize: 16,
    [theme.breakpoints.up("sm")]: {
      fontSize: 18,
    },
  },
  actionContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  actionBtn: {
    marginLeft: theme.spacing(2),
  },
  bookBtn: {
    width: 180,
  },
  calendarIcon: {
    width: 64,
    height: 64,
  },
  greeting: {
    marginLeft: theme.spacing(2),
    paddingBottom: theme.spacing(1),
    borderBottom: `1px solid ${theme.colors.MidnightBlue(100)}`,
  },
}));

type PitchPageInterviewSchedulerProps = {
  greeting?: string;
  slug: string;
  pitchPageId: string;
  candidateName?: string;
  disabled?: boolean;
};

const PitchPageInterviewScheduler: React.FC<
  PitchPageInterviewSchedulerProps
> = ({ greeting, slug, pitchPageId, candidateName, disabled = false }) => {
  const analytics = useAnalytics();
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const [schedulerMode, setSchedulerMode] = useState<SchedulerMode>(
    SchedulerMode.Schedule
  );

  const { interviewScheduler: pollInterval } = usePollingIntervalConfig();
  const { data, loading } = useQuery<
    GetAvailabilitySchedulerDataForPitchPage,
    GetAvailabilitySchedulerDataForPitchPageVariables
  >(AVAILABILITY_SCHEDULER_DATA, {
    variables: {
      pitchPageId,
    },
    pollInterval,
  });
  const event = data?.schedulerAvailabilityEventFindFirst;
  const isSamplePitchPage =
    !!data?.schedulerAvailabilityEventFindFirst?.pitchPage?.isSample;

  // Handle cancelled
  useEffect(() => {
    if (
      (schedulerMode === SchedulerMode.Cancel ||
        schedulerMode === SchedulerMode.Reschedule) &&
      !event
    ) {
      setOpen(false);
      setSchedulerMode(SchedulerMode.Schedule);
    }
  }, [event, schedulerMode]);

  // Handle cancelled
  useEffect(() => {
    if (schedulerMode === SchedulerMode.Cancel && !event) {
      setOpen(false);
      setSchedulerMode(SchedulerMode.Schedule);
    }
  }, [event, schedulerMode, slug]);

  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  let startTimeWithTimezone: Moment | undefined;
  let startTime: string | undefined;
  if (event?.startTime) {
    startTimeWithTimezone = moment.tz(event.startTime, timezone);
    startTime = `${startTimeWithTimezone.format(
      "MMMM Do"
    )} at ${startTimeWithTimezone.format(
      "h:mm a"
    )} ${startTimeWithTimezone.format("z")}`;
  }

  const reschedule = () => {
    analytics.track("PP Scheduler Reschedule Interview Clicked");
    setSchedulerMode(SchedulerMode.Reschedule);
    setOpen(true);
  };

  const cancel = () => {
    analytics.track("PP Scheduler Cancel Interview Clicked");
    setSchedulerMode(SchedulerMode.Cancel);
    setOpen(true);
  };

  if (loading) {
    return (
      <Paper className={classes.card}>
        <Loading />
      </Paper>
    );
  }

  return (
    <div className={classes.root}>
      <div className={classes.card}>
        <div className={classes.infoHeader}>
          <Image
            src={calendarIcon}
            className={classes.calendarIcon}
            alt="Calendar"
            layout="fixed"
            width="64px"
            height="64px"
          />
          <div className={classes.greeting}>
            <Typography variant="h4" className={classes.infoText}>
              {event ? (
                <span>You&apos;re confirmed for {startTime}</span>
              ) : (
                <span>
                  {greeting ||
                    "Please book a slot on my calendar - I’m looking forward to meeting with you!"}
                </span>
              )}
            </Typography>
          </div>
        </div>
        <div className={classes.actionContainer}>
          {event ? (
            <>
              <Button
                variant="contained"
                color="primary"
                className={classes.actionBtn}
                onClick={reschedule}
              >
                Find a Better Time
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                className={classes.actionBtn}
                onClick={cancel}
              >
                Cancel
              </Button>
            </>
          ) : disabled || isSamplePitchPage ? (
            <Tooltip title="Candidates will be directed to book on your calendar">
              <Button
                variant="contained"
                color="primary"
                className={(classes.actionBtn, classes.bookBtn)}
              >
                Book Time with Me
              </Button>
            </Tooltip>
          ) : (
            <Button
              variant="contained"
              color="primary"
              className={(classes.actionBtn, classes.bookBtn)}
              onClick={() => {
                analytics.track("PP Scheduler Book Interview Clicked");
                setOpen(true);
              }}
            >
              Book Time with Me
            </Button>
          )}
        </div>
        <NylasSchedulerModal
          mode={schedulerMode}
          candidateName={candidateName}
          slug={slug}
          editHash={event?.externalEditHash}
          open={open}
          pitchPageId={pitchPageId}
          onClose={() => setOpen(false)}
          onRedirect={(params: RedirectParams) => {
            if (params.pitchPageId) {
              setOpen(false);
            }
          }}
        />
      </div>
    </div>
  );
};

export default PitchPageInterviewScheduler;
